import { taskApi } from '@core/api';
import { LinkResources } from '@core/api/endpoints/resource/link_resources';
import type { Document } from '@core/types/document';
import type { Entity } from '@core/types/entity';
import type { PaginatedRequest } from '@core/types/pagination';
import type { CreateTaskRequest, Task } from '@core/types/task';

export const DOCUMENT_TASK_ID_MISSING = 'DOCUMENT_TASK_ID_MISSING';
export const DOCUMENT_TASKS_PAGE_SIZE = 50;

export interface DocumentTaskDraft {
  title: string;
  description: string;
  assignee: string;
  assigneeName: string;
  priority: NonNullable<CreateTaskRequest['priority']>;
}

export interface DocumentTaskSaveResult {
  taskId: string;
  linkError: unknown | null;
}

export interface DocumentTasksPage {
  tasks: Task[];
  nextCursor: string;
  hasNext: boolean;
}

export function documentParent(document: Pick<Document, 'entity'>): Entity {
  const entity = document.entity;
  if (!entity?.id) throw new Error('Document entity is required');
  return {
    type_id: entity.type_id || 'document',
    type_name: 'document',
    id: entity.id,
    platform_id: entity.platform_id || '',
    tenant_id: entity.tenant_id || '',
    product_id: entity.product_id || '',
  };
}

export function buildDocumentTaskDraft(
  document: Pick<Document, 'entity' | 'title' | 'description' | 'owner'>,
  sourceLabel: string,
): DocumentTaskDraft {
  const parent = documentParent(document);
  const source = `${sourceLabel}: /documents/${parent.id}`;
  return {
    title: (document.title || '').trim().slice(0, 120),
    description: [document.description?.trim(), source].filter(Boolean).join('\n\n'),
    assignee: document.owner?.entity?.id || '',
    assigneeName: document.owner?.display_name || '',
    priority: 'TASK_PRIORITY_MEDIUM',
  };
}

export function buildDocumentTaskRequest(
  document: Pick<Document, 'entity'>,
  draft: DocumentTaskDraft,
): CreateTaskRequest {
  const parent = documentParent(document);
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    type: 'TASK_TYPE_TASK',
    priority: draft.priority,
    parent,
    assignee: draft.assignee ? {
      entity: {
        ...parent,
        type_id: 'profile',
        type_name: 'profile',
        id: draft.assignee,
      },
      display_name: draft.assigneeName,
      avatar: '',
    } : undefined,
  };
}

function isAlreadyLinkedError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase().includes('link already exists');
}

export async function saveDocumentTask(
  document: Pick<Document, 'entity'>,
  draft: DocumentTaskDraft,
  existingTaskId?: string,
): Promise<DocumentTaskSaveResult> {
  const parent = documentParent(document);
  let taskId = existingTaskId || '';
  let taskEntity: Entity | undefined;

  if (!taskId) {
    const response = await taskApi.CreateTask(buildDocumentTaskRequest(document, draft));
    taskEntity = response.task?.entity;
    taskId = taskEntity?.id || '';
    if (!taskId) throw new Error(DOCUMENT_TASK_ID_MISSING);
  }

  const target: Entity = taskEntity ? {
    ...taskEntity,
    type_id: taskEntity.type_id || 'task',
    type_name: taskEntity.type_name || 'task',
  } : {
    ...parent,
    type_id: 'task',
    type_name: 'task',
    id: taskId,
  };

  try {
    await LinkResources({
      source: parent,
      target,
      relation_type: 'RELATION_TYPE_REFERENCES',
      relation_label: 'task_created_from',
    });
    return { taskId, linkError: null };
  } catch (error) {
    if (isAlreadyLinkedError(error)) return { taskId, linkError: null };
    return { taskId, linkError: error };
  }
}

function isDocumentTask(task: Task, documentId: string): boolean {
  const parent = task.parent;
  const kind = (parent?.type_name || parent?.type_id || '').replace(/^core\./, '');
  return parent?.id === documentId && kind === 'document';
}

export async function loadDocumentTasks(documentId: string, cursor = ''): Promise<DocumentTasksPage> {
  const params: PaginatedRequest = {
    pagination: { limit: DOCUMENT_TASKS_PAGE_SIZE, cursor, direction: 'forward' },
    view: 2,
    sort: [],
  };
  const response = await taskApi.ListTasksByParent('document', documentId, params);
  const pageInfo = response.pagination?.page_info;
  return {
    tasks: (response.tasks?.tasks || []).filter((task) => isDocumentTask(task, documentId)),
    nextCursor: pageInfo?.has_next ? pageInfo.next_cursor || '' : '',
    hasNext: Boolean(pageInfo?.has_next && pageInfo.next_cursor),
  };
}

export function mergeDocumentTasks(current: Task[], incoming: Task[]): Task[] {
  const merged = new Map<string, Task>();
  for (const task of [...current, ...incoming]) {
    const id = task.entity?.id;
    if (id) merged.set(id, task);
  }
  return [...merged.values()];
}
